import { supabase } from "@/lib/supabaseClient";

// ---------------------------------------------------------------------------
// Pipeline del socio: línea de tiempo, resumen, intervención humana y derivación.
// ---------------------------------------------------------------------------

export type TimelineEvent = { id: string; kind: string; label: string; createdAt: string };

export async function fetchTimeline(prospectId: string): Promise<TimelineEvent[]> {
  const { data, error } = await supabase
    .from("prospect_timeline")
    .select("id, kind, label, created_at")
    .eq("prospect_id", prospectId)
    .order("created_at", { ascending: true })
    .limit(200);
  if (error) throw error;
  return (data ?? []).map((r) => ({ id: r.id, kind: r.kind, label: r.label, createdAt: r.created_at }));
}

export type PipelineSummary = { total: number; needsHuman: number; stages: Record<string, number> };

export async function fetchPipelineSummary(): Promise<PipelineSummary> {
  const { data, error } = await supabase.rpc("my_pipeline_summary");
  if (error) throw error;
  const d = (data ?? {}) as { total?: number; needs_human?: number; stages?: Record<string, number> };
  return { total: d.total ?? 0, needsHuman: d.needs_human ?? 0, stages: d.stages ?? {} };
}

export async function setProspectStage(id: string, stage: string): Promise<void> {
  const { error } = await supabase.from("prospects").update({ pipeline_stage: stage }).eq("id", id);
  if (error) throw error;
}

/** "Ya lo atendí": quita la marca de intervención humana. */
export async function resolveNeedsHuman(id: string): Promise<void> {
  const { error } = await supabase.from("prospects").update({ needs_human: false, needs_human_reason: null, handled_by: "human" }).eq("id", id);
  if (error) throw error;
}

/** Marca a mano que este contacto necesita atención personal. */
export async function flagNeedsHuman(id: string, reason: string): Promise<void> {
  const { error } = await supabase
    .from("prospects")
    .update({ needs_human: true, needs_human_reason: reason.slice(0, 140), needs_human_at: new Date().toISOString(), handled_by: "human" })
    .eq("id", id);
  if (error) throw error;
}

export type ReassignTarget = { id: string; name: string; relation: "sponsor" | "equipo" | "admin" };

export async function fetchReassignTargets(): Promise<ReassignTarget[]> {
  const { data, error } = await supabase.rpc("my_reassign_targets");
  if (error) throw error;
  return ((data ?? []) as { id: string; full_name: string; relation: ReassignTarget["relation"] }[]).map((r) => ({ id: r.id, name: r.full_name, relation: r.relation }));
}

export async function reassignProspect(prospectId: string, toUserId: string): Promise<void> {
  const { error } = await supabase.rpc("prospect_reassign", { p_prospect: prospectId, p_to: toUserId });
  if (error) throw new Error(error.message.includes("forbidden") ? "No puedes derivar a esa persona." : "No se pudo derivar.");
}

export type ThreadMessage = { id: string; mine: boolean; who: "contact" | "bot" | "human" | "template"; body: string; createdAt: string };

/** Hilo de conversación de un prospecto: WhatsApp (wa_messages) o mensajes directos (prospect_messages). */
export async function fetchProspectThread(prospectId: string, isWhatsApp: boolean): Promise<{ messages: ThreadMessage[]; conversationId: string | null; lastInboundAt: string | null; status: string | null }> {
  if (isWhatsApp) {
    const { data: conv } = await supabase.from("wa_conversations").select("id, last_inbound_at, status").eq("prospect_id", prospectId).maybeSingle();
    if (!conv) return { messages: [], conversationId: null, lastInboundAt: null, status: null };
    const { data } = await supabase.from("wa_messages").select("id, direction, sender, body, created_at").eq("conversation_id", conv.id).order("created_at", { ascending: true }).limit(120);
    return {
      messages: (data ?? []).map((m) => ({ id: m.id, mine: m.direction === "out", who: m.sender, body: m.body, createdAt: m.created_at })),
      conversationId: conv.id,
      lastInboundAt: conv.last_inbound_at,
      status: conv.status,
    };
  }
  const { data } = await supabase.from("prospect_messages").select("id, direction, body, created_at").eq("prospect_id", prospectId).in("direction", ["in", "out"]).order("created_at", { ascending: true }).limit(120);
  return {
    messages: (data ?? []).map((m) => ({ id: m.id, mine: m.direction === "out", who: m.direction === "out" ? ("human" as const) : ("contact" as const), body: m.body, createdAt: m.created_at })),
    conversationId: null,
    lastInboundAt: null,
    status: null,
  };
}
